import './Form.scss'
import { useForm } from 'react-hook-form'
import { Field, Input, Switch } from '@fluentui/react-components'
import { useDotnetClient } from '@/contexts/DotnetClientContext'
import { Button } from '../Button'
import { toast } from 'react-toastify'
import { useEffect, useState } from 'react'
import { OverlayDrawerComponent } from '../OverlayDrawerComponent/OverlayDrawerComponent'
import { FileUpload } from './FileUpload'
import { ENDPOINTS } from '@/util'
import { RestSchemas } from '@/contexts/QueryProvider'
import DOMPurify from 'isomorphic-dompurify'
import { useSessionStorage } from '@/hooks/useSessionStorage'

type MessagesEntityTypesUpdateEntityType = RestSchemas['Messages.EntityTypes.UpdateEntityType']
type MessagesTemplatesFileTemplate = RestSchemas['Messages.Templates.FileTemplate']
type MessagesTemplatesNewTemplate = RestSchemas['Messages.Templates.NewTemplate']
type MessagesStorageStorageFileType = RestSchemas['Messages.Storage.StorageFileType']

type AddFileTemplateFormProps = {
  applicationId: number
  selectedEntityType?: MessagesEntityTypesUpdateEntityType
  selectedTemplate?: MessagesTemplatesFileTemplate
  edit?: boolean
  delete?: boolean
  onSubmit: () => void
  onDelete?: () => void // Optional delete callback
  invalidateTemplates?: () => void
}

type TFileTemplateForm = {
  id?: number
  name: string
  description: string
  key: string
  requireApprovals: boolean
}

export const FileTemplateForm = (props: AddFileTemplateFormProps) => {
  const { dmpClient, isDMPClientLoaded } = useDotnetClient()
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)
  const [token] = useSessionStorage('token', '')

  const {
    register,
    formState: { errors },
    reset,
    handleSubmit,
  } = useForm<TFileTemplateForm>({
    defaultValues:
      (props.edit || props.delete) === true && props.selectedTemplate
        ? ({
            ...props.selectedTemplate,
          } as TFileTemplateForm)
        : ({
            name: '',
            description: '',
            key: '',
            requireApprovals: true,
          } as TFileTemplateForm),
  })

  useEffect(() => {
    if (isDMPClientLoaded) {
      reset()
    }
  }, [isDMPClientLoaded, reset])

  useEffect(() => {
    reset()
  }, [props.selectedTemplate, reset])

  const onSubmit = (data: TFileTemplateForm) => {
    if (props.edit && props.selectedTemplate) {
      const updatedTemplate: MessagesTemplatesFileTemplate = {
        ...props.selectedTemplate,
        ...data,
      }

      dmpClient?.templatesApi
        .postTemplatesApiUpdateTemplate(updatedTemplate)
        .then(() => {
          toast.success('Template updated')
          props.onSubmit()
          reset()
        })
        .catch((err) => {
          console.error(err)
          toast.error('Error updating template')
          props.onSubmit()
        })
    } else {
      const newTemplate: MessagesTemplatesNewTemplate = {
        entityTypeId: props.selectedEntityType!.id,
        applicationId: props.applicationId,
        ...data,
      }

      dmpClient?.templatesApi
        .putTemplatesApiAddTemplate(newTemplate)
        .then(() => {
          toast.success('Template added')
          props.onSubmit()
          reset()
        })
        .catch((err) => {
          console.error(err)
          toast.error('Error adding template')
          props.onSubmit()
        })
    }
  }

  const handleDelete = async () => {
    try {
      await dmpClient?.templatesApi.deleteTemplatesApiDeleteTemplate(props.selectedTemplate)
      toast.success('Template successfully deleted')
      props.onDelete!() // Ensure this does not cause the parent component to unmount immediately
      setIsDeleteConfirmOpen(false) // Close the drawer after toast displays
    } catch (err) {
      console.error('Error deleting template:', err)
      toast.error('Error deleting template')
    }
  }

  const onDownloadClick = async () => {
    const fileType: MessagesStorageStorageFileType = 1

    try {
      const response = await fetch(
        `${ENDPOINTS.LANDING}/storage/download_file/${props.selectedTemplate!.templateFileUrl},${props.applicationId},${0},${fileType}`,
        {
          headers: { Authorization: 'Bearer ' + token },
        }
      )
      if (response.ok) {
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.setAttribute('href', url)
        a.setAttribute('download', props.selectedTemplate!.templateFilename ?? 'filename.txt')
        document.body.appendChild(a)
        a.click()
        a.remove()
      } else {
        let errMessage = 'Unknown error'
        if (response.statusText) errMessage = response.statusText
        try {
          const tryJson = await response.json()
          if (tryJson.Message) errMessage = tryJson.Message
        } catch (error) {
          // ignore
        }
        toast.error('Error downloading file: ' + DOMPurify.sanitize(errMessage))
      }
    } catch (error) {
      toast.error(`Error downloading file: ' + ${DOMPurify.sanitize(`${error}`)}`)
      console.error(DOMPurify.sanitize(`${error}`))
    }
  }

  return (
    <div className="form-wrapper">
      <div>
        {/* Name Field */}
        <Field
          label="Name"
          required
          validationState={errors.name ? 'error' : 'none'}
          validationMessage={errors.name?.message}
        >
          <Input
            {...register('name', {
              required: 'Name is required',
              disabled: props.delete,
              maxLength: 100,
              setValueAs: (value) => value ?? undefined,
            })}
          />
        </Field>

        {/* Description Field */}
        <Field
          label="Description"
          required
          validationState={errors.description ? 'error' : 'none'}
          validationMessage={errors.description?.message}
        >
          <Input
            {...register('description', {
              required: 'Description is required',
              disabled: props.delete,
              maxLength: 200,
              setValueAs: (value) => value ?? undefined,
            })}
          />
        </Field>

        <Field
          label="Key"
          required
          validationState={errors.description ? 'error' : 'none'}
          validationMessage={errors.description?.message}
        >
          <Input
            {...register('key', {
              maxLength: 200,
              disabled: props.delete,
              setValueAs: (value) => value ?? undefined,
            })}
          />
        </Field>

        <Field
          label="Require Approvals?"
          required
          validationState={errors.description ? 'error' : 'none'}
          validationMessage={errors.description?.message}
        >
          <Switch {...register('requireApprovals', { disabled: props.delete })} />
        </Field>

        {props.edit && props.selectedTemplate && (
          <>
            <div className="file-upload-block">
              Upload an example file:
              <FileUpload
                onSubmit={() => {
                  if (props.invalidateTemplates) props.invalidateTemplates()
                }}
                applicationId={props.applicationId}
                templateId={props.selectedTemplate!.id}
                projectId={0}
                entityId={0}
              />
            </div>
            <div>
              Current example:{' '}
              {props.selectedTemplate.templateFilename ? (
                <a onClick={onDownloadClick}>{props.selectedTemplate.templateFilename}</a>
              ) : (
                'none'
              )}
            </div>
          </>
        )}
      </div>

      {/* Submit Button */}
      <div className="form-action-container">
        {props.delete && props.onDelete && <Button text="Delete" onClick={() => setIsDeleteConfirmOpen(true)} />}
        {!props.delete && <Button text="Submit" onClick={handleSubmit(onSubmit)} />}
      </div>

      {/* Confirmation Modal for Delete */}
      <OverlayDrawerComponent
        open={isDeleteConfirmOpen}
        setOpen={setIsDeleteConfirmOpen}
        title="Confirm Delete"
        content={
          <div>
            <p>Are you sure you want to delete this file template?</p>
            <Button text="Confirm" onClick={handleDelete} />
          </div>
        }
      />
    </div>
  )
}
